import { darken, readableColor, rem } from 'polished';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { translate } from '@vimeo/i18n-runtime';
import { Button } from '@vimeo/iris/components';
import { Play as PlayIcon, Search as SearchIcon } from '@vimeo/iris/icons';
import { themes } from '@vimeo/iris/themes';

import * as translations from '../../../translations';
import { EmbedShowcaseContext } from '../../context/useEmbedShowcase';
import { Pictures } from '../../types';
import { getRawUploadedImageUrl } from '../../utils/helpers';
import { AlbumDescriptionWithLinks } from '../AlbumHeader/AlbumDescriptionWithLinks';
import TeamMenu from '../LiveNav/TeamMenu';
import Search from '../Search';
import { useSearchState } from '../SearchContext';
import {
  AlbumInfo,
  CountAndShareButton,
  CustomLogoWrapper,
  InfoBar,
  MegaHeader,
  VideoCount,
  SearchButton,
  ShareIcon,
  StartWatchingButton,
} from './PlayerLayoutNavStyled';

const InfoContainer = styled.div`
  width: 100%;
  max-width: ${rem(1600)};
  margin: 0 auto;
`;

interface Props {
  albumName: string;
  brandColor: string;
  description: string;
  customLogo: Pictures;
  webCustomLogo: boolean;
  onPlay: () => void;
  openShare: VoidFunction;
  videoTotal: number;
  allowShare: boolean;
  albumOwnerId?: number;
  teamRole?: string;
  thumbnail?: string;
}

/**
 * Render custom nav for player layout
 */
export default function PlayerLayoutNav({
  albumName,
  brandColor,
  customLogo,
  webCustomLogo,
  description,
  onPlay,
  openShare,
  videoTotal = 0,
  allowShare,
  albumOwnerId,
  teamRole,
  thumbnail,
}: Props) {
  const useCustomLogo = webCustomLogo && customLogo && customLogo.active;
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  const { isSearching } = useSearchState();
  const [searchBarIsShowing, setShowSearchBar] = useState(false);
  // Grab the logo's pure image path and then manipulate it below in the return
  const customLogoSrc = useCustomLogo ? getRawUploadedImageUrl(customLogo) : undefined;

  function trackOpenSearch(role?: string) {
    FA.trackClick({
      container: 'showcase',
      component: 'search',
      keyword: 'open_search',
    });
    if (role) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'search',
        keyword: 'open_search_' + role.toLowerCase(),
      });
    }
  }

  return (
    <InfoBar isEmpty={videoTotal === 0} isSearching={isSearching} thumbnail={thumbnail}>
      <InfoContainer>
        <CountAndShareButton isSearching={isSearching}>
          {albumOwnerId && teamRole && !embedShowcaseData?.isShowcaseEmbeded && (
            <TeamMenu ownerId={albumOwnerId} userRole={teamRole} />
          )}
          {!searchBarIsShowing && (
            <SearchButton
              aria-label={translate('Search')}
              format="secondary"
              theme={themes.dark}
              icon={<SearchIcon />}
              onClick={() => {
                trackOpenSearch(teamRole);
                setShowSearchBar(true);
              }}
            />
          )}
          {searchBarIsShowing && <Search setShowSearchBar={setShowSearchBar} teamRole={teamRole} />}
          <VideoCount>{translations.videoCount(videoTotal)}</VideoCount>
          {allowShare && (
            <Button
              format="basic"
              variant="hyperminimal"
              onClick={() => {
                if (teamRole) {
                  FA.trackClick({
                    container: 'team_home_page',
                    component: 'share_modal',
                    keyword: 'open_share_' + teamRole.toLowerCase(),
                  });
                }
                openShare();
              }}
              size="sm"
              icon={<ShareIcon />}
            />
          )}
        </CountAndShareButton>
        {useCustomLogo && !isSearching && (
          <CustomLogoWrapper>
            <img src={customLogoSrc} alt={albumName} />
          </CustomLogoWrapper>
        )}
        <AlbumInfo>
          <MegaHeader theme={themes.dark}>{albumName}</MegaHeader>
          {description && (
            <AlbumDescriptionWithLinks theme={themes.dark}>{description}</AlbumDescriptionWithLinks>
          )}
          {videoTotal > 0 && (
            <StartWatchingButton
              format="primary"
              theme={{ ...themes.dark, formats: { primary: brandColor } }}
              icon={<PlayIcon style={{ height: rem(18), width: rem(18), marginRight: rem(4) }} />}
              onClick={() => {
                onPlay();
                FA.trackClick({
                  container: 'showcase',
                  component: 'start_watching',
                });
              }}
              size="md"
            >
              {translate('Start watching')}
            </StartWatchingButton>
          )}
        </AlbumInfo>
      </InfoContainer>
    </InfoBar>
  );
}
